import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Bell,
    Flame,
    Sparkles,
    PartyPopper,
    Check,
    UserPlus,
    MessageCircle,
    Bookmark,
    Mountain,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { api } from "../lib/api";
import { useRealtime } from "../lib/RealtimeContext";
import { formatDistanceToNow } from "date-fns";

function milestoneIcon(n) {
    if (!n) return Sparkles;
    if (n >= 50) return Flame;
    if (n >= 10) return PartyPopper;
    return Sparkles;
}

function iconForNotification(n) {
    switch (n.kind) {
        case "welcome":
            return Mountain;
        case "new_follower":
            return UserPlus;
        case "comment":
            return MessageCircle;
        case "save":
            return Bookmark;
        case "message":
            return MessageCircle;
        case "share_milestone":
            return milestoneIcon(n.threshold);
        default:
            return Bell;
    }
}

export default function NotificationBell({ variant = "sidebar" }) {
    const navigate = useNavigate();
    const { subscribe, connected } = useRealtime();
    const [open, setOpen] = useState(false);
    const [unread, setUnread] = useState(0);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const pollRef = useRef(null);

    const fetchUnread = useCallback(async () => {
        try {
            const { data } = await api.get("/notifications/unread-count");
            setUnread(data.count || 0);
        } catch {
            // silent — user may have logged out
        }
    }, []);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/notifications", { params: { limit: 15 } });
            setItems(data);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUnread();
        // When WS is connected we stop polling; we still keep a slow
        // background poll when disconnected as a fallback.
        if (!connected) {
            pollRef.current = setInterval(fetchUnread, 60000);
        }
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [fetchUnread, connected]);

    // Realtime: prepend new notifications + bump unread badge live.
    useEffect(() => {
        const off = subscribe("notification", (n) => {
            setUnread((u) => u + 1);
            setItems((xs) => [{ ...n, read: false }, ...xs].slice(0, 30));
        });
        return off;
    }, [subscribe]);

    const onOpenChange = (o) => {
        setOpen(o);
        if (o) fetchList();
    };

    const onClick = async (n) => {
        try {
            if (!n.read) {
                await api.patch(`/notifications/${n.id}/read`);
                setUnread((c) => Math.max(0, c - 1));
                setItems((xs) => xs.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
            }
        } catch {
            // ignore — still navigate
        }
        setOpen(false);
        if (n.link) navigate(n.link);
    };

    const markAll = async () => {
        try {
            await api.post("/notifications/read-all");
            setItems((xs) => xs.map((x) => ({ ...x, read: true })));
            setUnread(0);
        } catch {
            // ignore
        }
    };

    const iconClasses =
        variant === "topbar"
            ? "relative w-10 h-10 rounded-md flex items-center justify-center text-granite hover:bg-alpine-mist transition-colors"
            : "relative w-full flex items-center gap-3 px-4 py-3 rounded-lg text-granite hover:bg-trail-tan/50 transition-all";

    return (
        <Popover open={open} onOpenChange={onOpenChange}>
            <PopoverTrigger asChild>
                <button data-testid="notifications-bell" className={iconClasses}>
                    <span className="relative shrink-0">
                        <Bell className="w-5 h-5" />
                        {unread > 0 && (
                            <span
                                data-testid="notifications-badge"
                                className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-ember-orange text-white text-[10px] font-bold leading-[18px] text-center"
                            >
                                {unread > 9 ? "9+" : unread}
                            </span>
                        )}
                    </span>
                    {variant === "sidebar" && (
                        <span className="font-semibold text-sm">
                            Notifications
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                sideOffset={8}
                className="w-[340px] p-0 bg-snowcap border-ridge-line"
                data-testid="notifications-panel"
            >
                <div className="flex items-center justify-between px-4 py-3 border-b border-ridge-line">
                    <div className="font-display font-bold text-midnight-pine">Notifications</div>
                    {items.some((i) => !i.read) && (
                        <button
                            data-testid="notifications-mark-all"
                            onClick={markAll}
                            className="text-xs font-bold text-ember-orange hover:underline"
                        >
                            Mark all read
                        </button>
                    )}
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                    {loading ? (
                        <div className="p-6 text-center text-sm text-slate-gray">Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="p-8 text-center">
                            <Bell className="w-8 h-8 text-slate-gray mx-auto mb-3 opacity-50" />
                            <p className="text-sm text-slate-gray">
                                You're all caught up. Shares & milestones will show up here.
                            </p>
                        </div>
                    ) : (
                        <ul>
                            {items.map((n) => {
                                const Icon = iconForNotification(n);
                                return (
                                    <li key={n.id}>
                                        <button
                                            data-testid={`notification-${n.id}`}
                                            onClick={() => onClick(n)}
                                            className={`w-full flex items-start gap-3 px-4 py-3 text-left border-b border-ridge-line last:border-b-0 hover:bg-alpine-mist transition-colors ${
                                                !n.read ? "bg-ember-orange/5" : ""
                                            }`}
                                        >
                                            <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${
                                                !n.read ? "bg-ember-orange text-white" : "bg-trail-tan text-midnight-pine"
                                            }`}>
                                                <Icon className="w-4 h-4" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-bold text-granite leading-tight">
                                                    {n.title}
                                                </p>
                                                {n.body && (
                                                    <p className="text-xs text-slate-gray mt-1 line-clamp-2">
                                                        {n.body}
                                                    </p>
                                                )}
                                                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-gray mt-1">
                                                    {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                                                </p>
                                            </div>
                                            {n.read && <Check className="w-3.5 h-3.5 text-slate-gray shrink-0 mt-1" />}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
